/**
 * Moroccan mobile phone numbers.
 *
 * Canonical (stored) form is E.164: "+2126XXXXXXXX" or "+2127XXXXXXXX".
 * Only mobile ranges (06 / 07) are accepted because the number must be able
 * to receive SMS verification codes.
 */

const ARABIC_INDIC = "٠١٢٣٤٥٦٧٨٩";
const EXTENDED_ARABIC_INDIC = "۰۱۲۳۴۵۶۷۸۹";

/** Converts Arabic-Indic digits (typed on Arabic keyboards) to ASCII digits. */
export function toAsciiDigits(input: string): string {
  return input.replace(/[٠-٩۰-۹]/g, (ch) => {
    const a = ARABIC_INDIC.indexOf(ch);
    if (a >= 0) return String(a);
    return String(EXTENDED_ARABIC_INDIC.indexOf(ch));
  });
}

/**
 * Normalizes any common way of writing a Moroccan mobile number into E.164.
 * Accepts: 0612345678, 06 12 34 56 78, +212612345678, +212 0612345678,
 * 00212612345678, 212612345678. Returns null when the input is not a valid
 * Moroccan mobile number.
 */
export function normalizeMoroccanPhone(input: string | null | undefined): string | null {
  if (typeof input !== "string") return null;
  let s = toAsciiDigits(input.trim()).replace(/[\s\-().‎‏]/g, "");
  if (s.length === 0 || s.length > 20) return null;
  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (s.startsWith("+")) {
    if (!s.startsWith("+212")) return null;
    s = s.slice(4);
  } else if (s.startsWith("212") && (s.length === 12 || s.length === 13)) {
    s = s.slice(3);
  }
  if (s.startsWith("0")) s = s.slice(1);
  if (!/^[67]\d{8}$/.test(s)) return null;
  return "+212" + s;
}

export function isValidMoroccanPhone(input: string | null | undefined): boolean {
  return normalizeMoroccanPhone(input) !== null;
}

/** "+212612345678" -> "06 12 34 56 78" (national display format). */
export function formatMoroccanPhone(e164: string): string {
  const n = normalizeMoroccanPhone(e164);
  if (!n) return e164;
  const local = "0" + n.slice(4);
  return local.replace(/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/, "$1 $2 $3 $4 $5");
}

/** Masks a phone for display: "06 •• •• •• 78". */
export function maskMoroccanPhone(e164: string): string {
  const f = formatMoroccanPhone(e164);
  return f.replace(/^(\d{2}) \d{2} \d{2} \d{2} (\d{2})$/, "$1 •• •• •• $2");
}
